import React from 'react';
import { translate } from 'react-i18next';
import styled from 'styled-components';
import Divider from 'material-ui/Divider';

import { Numeric, displayCurrency } from 'common/components/Number';
import Opportunity from 'common/modules/proposition/domain/opportunity';
import { formatDays } from 'common/domain/week';
import Card from 'common/components/Card';

const ScreenCard = styled(Card)`
  ${({ theme }) => `
    padding: ${theme.metrics.margin}px;
    margin: ${theme.metrics.margin}px 0px;
    position: relative;
  `}
`;

const PrintCard = styled.div`
  ${({ theme }) => `
    padding: ${theme.metrics.margin}px;
    margin: ${theme.metrics.margin}px 0px;
    border: solid thin black;
    page-break-inside: avoid;
  `}
`;

const Row = styled.div`
  display: flex;
  justify-content: space-around;
  margin: ${({ theme }) => theme.metrics.tinyMargin}px 0px;
  width: 100%;
  align-items: center;
`;

const Text = styled.div`
  color: ${({ theme }) => theme.colors.primaryText};
  font-size: 16px;
  text-align: center;
  min-width: 23px;
  vertical-align: middle;
`;

const Capitalized = styled(Text)`
  &:first-letter {
    text-transform: uppercase;
  }
`;

const Bolder = styled.span`
  font-weight: ${({ theme }) => theme.fonts.weights.bolder};
`;

const DescriptionText = styled(Text)`
  text-align: left;
  margin-top: ${({ theme }) => theme.metrics.smallMargin}px;
  align-self: flex-start;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const MetricTitle = styled.div`
  ${({ theme }) => `
    margin-bottom: 8px;
    color: ${theme.colors.primaryText};
    font-size: ${theme.fonts.style.midSmall.fontSize}px;
  `}
`;

const MetricNumber = styled.div`
  ${({ theme }) => `
    color: ${theme.colors.primary};
    font-size: ${theme.fonts.style.smallCardTitle.fontSize}px;
  `}
`;

const OpportunityContainer = styled.div`
  width: 100%;
`;

type Props = {
  opportunity: Opportunity;
  index: number;
  printing: boolean;

  t: any;
}

function OpportunityCard({ t, opportunity, index, printing }: Props) {
  const dateRangeTranslation = t('simpleDateRange', { from: opportunity.fromDate, to: opportunity.toDate });
  const daysTranslation = formatDays(opportunity.days, { separator: ' - ', format: 'SHORT' });
  const timeRangeTranslation = t('simpleTimeRange', { from: opportunity.startTime, to: opportunity.endTime });
  const cost = opportunity.costPerOpportunities !== undefined ? displayCurrency(opportunity.costPerOpportunities) : '-';

  const Wrapper = ({ children }) => (printing ? <PrintCard>{children}</PrintCard> : <ScreenCard>{children}</ScreenCard>);

  return (
    <Wrapper>
      <OpportunityContainer>
        <Row>
          <Text># {index}</Text>
          <Capitalized><Bolder>{`${t('dates')}: `}</Bolder>{dateRangeTranslation}</Capitalized>
          <Text><Bolder>{`${t('days')}: `}</Bolder>{daysTranslation}</Text>
          <Text><Bolder>{`${t('time.hours')}: `}</Bolder> {timeRangeTranslation}</Text>
        </Row>
        <Divider />
        <Row>
          <Metric>
            <MetricTitle>{t('oppPerWeek')}</MetricTitle>
            <MetricNumber>{opportunity.numberOfOpportunities || '-'}</MetricNumber>
          </Metric>
          <Metric>
            <MetricTitle>{t('costPerOpp')}</MetricTitle>
            <MetricNumber>{cost}</MetricNumber>
          </Metric>
          <Metric>
            <MetricTitle>{t('totalReachPerWeek')}</MetricTitle>
            <MetricNumber>{opportunity.reach ? <Numeric value={opportunity.reach} roundAt={0} /> : '-'}</MetricNumber>
          </Metric>
          <Metric>
            <MetricTitle>{t('averageFrequencyPerWeek')}</MetricTitle>
            <MetricNumber>{opportunity.averageFrequency ? <Numeric value={opportunity.averageFrequency} roundAt={2} /> : '-'}</MetricNumber>
          </Metric>
          <Metric>
            <MetricTitle>{t('impressionsPerWeek')}</MetricTitle>
            <MetricNumber>{opportunity.impressions ? <Numeric value={opportunity.impressions} roundAt={0} /> : '-'}</MetricNumber>
          </Metric>
        </Row>
        {opportunity.description &&
          <DescriptionText><Bolder>{t('description')}: </Bolder>{opportunity.description}</DescriptionText>
        }
      </OpportunityContainer>
    </Wrapper>
  );
}

export default translate('commonSales')(OpportunityCard);



// WEBPACK FOOTER //
// ./src/common/modules/sales/components/OpportunityCard.js